import React, { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import { renderAsync } from "docx-preview";


export default function WordPreviewModal({ doc, onClose }) {
  const mountRef = useRef(null);
  const [ready, setReady] = useState(false); 
  const [renderError, setRenderError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  // Converte qualunque cosa arrivi in ArrayBuffer
  const toArrayBuffer = (bufLike) => {
    if (!bufLike) return null;
    if (bufLike instanceof ArrayBuffer) return bufLike;
    // Uint8Array, Buffer, ecc.
    if (bufLike.buffer instanceof ArrayBuffer) return bufLike.buffer;
    try {
      return new Uint8Array(bufLike).buffer;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    setRenderError(null);
    // Se non ho doc o non sono “ready” o non ho il container, non faccio nulla
    if (!doc || !ready || !mountRef.current) return;

    let canceled = false;
    let objectUrl = null;

    // Costruisci il Blob partendo dal buffer
    const ab = toArrayBuffer(doc.buffer);
    if (!ab) {
      setRenderError("Formato dati non valido per l’anteprima.");
      return;
    }

    const mime =
      doc.mime ||
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

    const blob = new Blob([ab], { type: mime });
    objectUrl = URL.createObjectURL(blob);
    setDownloadUrl(objectUrl);

    // pulizia container
    if (mountRef.current) {
      try {
        mountRef.current.innerHTML = "";
      } catch {
        /* no-op */
      }
    }

    const options = {
      className: "docx-preview",
      inWrapper: true,
      ignoreFonts: false,
      breakPages: false,
      useMathML: false,
    };

    // Piccolo delay per essere sicuri che il nodo sia nel DOM
    const id = requestAnimationFrame(() => {
      if (!mountRef.current || canceled) return;
      renderAsync(blob, mountRef.current, null, options).catch((err) => {
        console.error("docx-preview error:", err);
        if (!canceled) {
          setRenderError("Impossibile mostrare l’anteprima del documento.");
        }
      });
    });

    return () => {
      canceled = true;
      cancelAnimationFrame(id);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (mountRef.current) {
        try {
          mountRef.current.innerHTML = "";
        } catch {
          /* no-op */
        }
      }
      setDownloadUrl(null);
    };
  }, [doc, ready]);

  const openInNewTab = () => {
    if (downloadUrl) window.open(downloadUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={!!doc}
      onClose={onClose}
      scroll="paper"
      TransitionProps={{
        onEntered: () => setReady(true),
        onExited: () => setReady(false),
      }}
    >
      {doc && (
        <>
          <DialogTitle sx={{ p: 1.5 }} className="flex justify-between items-center">
            <span className="font-semibold truncate">{doc.name || "Anteprima"}</span>
            <div className="flex items-center gap-2">
              <Button
                size="small"
                variant="outlined"
                onClick={openInNewTab}
                disabled={!downloadUrl}
                sx={{
                  color: "#6366f1",
                  borderColor: "#6366f1",
                  textTransform: "uppercase",
                  "&:hover": {
                    color: "#4f46e5",
                    borderColor: "#4f46e5",
                    backgroundColor: "rgba(99,102,241,0.06)",
                  },
                }}
              >
                Apri / scarica
              </Button>
              <IconButton onClick={onClose}>
                <XMarkIcon className="h-5 w-5" />
              </IconButton>
            </div>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              p: 0,
              "& .docx-preview": { padding: "16px", backgroundColor: "#fff" },
              minHeight: 300,
            }}
          >
            {renderError ? (
              <div className="p-4">
                {renderError}{" "}
                {downloadUrl && (
                  <a
                    className="text-indigo-600 underline"
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Apri / scarica il file
                  </a>
                )}
              </div>
            ) : (
              <div ref={mountRef} />
            )}
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", py: 2 }}>
 
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}